import { format } from 'date-fns';
import { useState } from 'react';

import {
    LaneFilterChips,
    OUTREACH_LANE_OPTIONS,
    type OutreachLaneKey,
} from '@apps/web/components/admin/outreach_observability/lane_filter';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { cn } from '@apps/web/lib/utils';
import {
    type AutoOutreachWeek,
    type AutoOutreachWeekLane,
    type AutoOutreachWeeklySection,
} from '@libraries/typescript/api_client';

// Selectable trailing spans; the default matches the backend's
// AUTO_OUTREACH_WEEKLY_WEEKS and the API caps the span at 12 weeks.
export const DEFAULT_WEEKLY_WEEKS = 4;
const WEEKLY_WEEKS_OPTIONS = [4, 8, 12] as const;

// One week's totals summed over the selected lanes.
type WeekTotals = {
    readonly weekStart: string;
    readonly outreachCount: number;
    readonly autoStartedCount: number;
    readonly autoMonitoredCount: number;
    readonly shiftsCovered: number;
    readonly shiftsFilled: number;
    readonly shiftsCancelled: number;
    readonly shiftsUnfilled: number;
    readonly yesCount: number;
    readonly noCount: number;
    readonly otherCount: number;
    readonly responsesReceived: number;
};

type MetricRow = {
    readonly key: string;
    readonly label: string;
    readonly kind: 'count' | 'rate';
    // Rates return null when the denominator is zero.
    readonly value: (week: WeekTotals) => number | null;
    // For count metrics, the share shown in parentheses next to the count
    // (e.g. yes responses as a share of responses received).
    readonly denominator?: (week: WeekTotals) => number;
    // Whether an increase reads as good (green) or bad (red); undefined
    // metrics are neutral and render their delta in muted gray.
    readonly increaseIsGood?: boolean;
};

const METRIC_ROWS: ReadonlyArray<MetricRow> = [
    {
        key: 'outreaches',
        label: 'Outreaches created',
        kind: 'count',
        value: (week) => week.outreachCount,
        increaseIsGood: true,
    },
    {
        key: 'yes_rate',
        label: 'Yes rate',
        kind: 'rate',
        value: (week) =>
            week.responsesReceived > 0
                ? week.yesCount / week.responsesReceived
                : null,
        increaseIsGood: true,
    },
    {
        key: 'yes_responses',
        label: 'Yes responses',
        kind: 'count',
        value: (week) => week.yesCount,
        denominator: (week) => week.responsesReceived,
        increaseIsGood: true,
    },
    {
        key: 'no_responses',
        label: 'No responses',
        kind: 'count',
        value: (week) => week.noCount,
        denominator: (week) => week.responsesReceived,
        increaseIsGood: false,
    },
    {
        key: 'other_responses',
        label: 'Other responses',
        kind: 'count',
        value: (week) => week.otherCount,
        denominator: (week) => week.responsesReceived,
    },
    {
        key: 'responses',
        label: 'Responses received',
        kind: 'count',
        value: (week) => week.responsesReceived,
        increaseIsGood: true,
    },
    {
        key: 'fill_rate',
        label: 'Shift fill rate',
        kind: 'rate',
        value: (week) =>
            week.shiftsCovered > 0
                ? week.shiftsFilled / week.shiftsCovered
                : null,
        increaseIsGood: true,
    },
    {
        key: 'shifts_filled',
        label: 'Shifts filled',
        kind: 'count',
        value: (week) => week.shiftsFilled,
        denominator: (week) => week.shiftsCovered,
        increaseIsGood: true,
    },
    {
        key: 'shifts_covered',
        label: 'Shifts covered',
        kind: 'count',
        value: (week) => week.shiftsCovered,
        increaseIsGood: true,
    },
    {
        key: 'shifts_cancelled',
        label: 'Shifts cancelled',
        kind: 'count',
        value: (week) => week.shiftsCancelled,
        denominator: (week) => week.shiftsCovered,
        increaseIsGood: false,
    },
    {
        key: 'shifts_unfilled',
        label: 'Shifts unfilled',
        kind: 'count',
        value: (week) => week.shiftsUnfilled,
        denominator: (week) => week.shiftsCovered,
        increaseIsGood: false,
    },
    {
        key: 'auto_started',
        label: 'Auto-started',
        kind: 'count',
        value: (week) => week.autoStartedCount,
        denominator: (week) => week.outreachCount,
        increaseIsGood: true,
    },
    {
        key: 'auto_monitored',
        label: 'Auto-monitored',
        kind: 'count',
        value: (week) => week.autoMonitoredCount,
        denominator: (week) => week.outreachCount,
        increaseIsGood: true,
    },
];

function sumLanes(
    week: AutoOutreachWeek,
    lanes: ReadonlySet<OutreachLaneKey>,
): WeekTotals {
    const selected: Array<AutoOutreachWeekLane> = OUTREACH_LANE_OPTIONS.filter(
        (option) => lanes.has(option.key),
    ).map((option) => week[option.key]);
    const total = (pick: (lane: AutoOutreachWeekLane) => number) =>
        selected.reduce((sum, lane) => sum + pick(lane), 0);
    return {
        weekStart: week.week_start,
        outreachCount: total((lane) => lane.outreach_count),
        autoStartedCount: total((lane) => lane.auto_started_count),
        autoMonitoredCount: total((lane) => lane.auto_monitored_count),
        shiftsCovered: total((lane) => lane.shifts_covered),
        shiftsFilled: total((lane) => lane.shifts_filled),
        shiftsCancelled: total((lane) => lane.shifts_cancelled),
        shiftsUnfilled: total((lane) => lane.shifts_unfilled),
        yesCount: total((lane) => lane.yes_count),
        noCount: total((lane) => lane.no_count ?? 0),
        otherCount: total((lane) => lane.other_count ?? 0),
        responsesReceived: total((lane) => lane.responses_received),
    };
}

function weekLabel(weekStart: string): string {
    const start = new Date(`${weekStart}T00:00:00`);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${format(start, 'M/d')} – ${format(end, 'M/d')}`;
}

function pctLabel(count: number, denominator: number): string {
    if (denominator <= 0) {
        return '0%';
    }
    return `${((count / denominator) * 100).toFixed(0)}%`;
}

function formatValue(value: number | null, kind: 'count' | 'rate'): string {
    if (value === null) {
        return '—';
    }
    if (kind === 'rate') {
        return `${(value * 100).toFixed(0)}%`;
    }
    return value.toLocaleString();
}

function DeltaBadge({
    current,
    previous,
    kind,
    increaseIsGood,
}: {
    readonly current: number | null;
    readonly previous: number | null;
    readonly kind: 'count' | 'rate';
    readonly increaseIsGood?: boolean;
}) {
    if (current === null || previous === null) {
        return null;
    }
    const delta = current - previous;
    if (delta === 0) {
        return <span className="text-muted-foreground text-[11px]">±0</span>;
    }
    const magnitude =
        kind === 'rate'
            ? // Rate diffs read as percentage-point changes.
              `${Math.abs(delta * 100).toFixed(1)}pp`
            : Math.abs(delta).toLocaleString();
    return (
        <span
            className={cn(
                'text-[11px] tabular-nums',
                increaseIsGood === undefined
                    ? 'text-muted-foreground'
                    : delta > 0 === increaseIsGood
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400',
            )}
        >
            {delta > 0 ? '▲' : '▼'} {magnitude}
        </span>
    );
}

function MetricCell({
    metric,
    week,
    previousWeek,
}: {
    readonly metric: MetricRow;
    readonly week: WeekTotals;
    readonly previousWeek: WeekTotals | undefined;
}) {
    const current = metric.value(week);
    const previous = previousWeek ? metric.value(previousWeek) : null;
    return (
        <td className="px-3 py-2 text-right align-top whitespace-nowrap">
            <span className="tabular-nums">
                {formatValue(current, metric.kind)}
                {metric.denominator && current !== null ? (
                    <span className="text-muted-foreground">
                        {' '}
                        ({pctLabel(current, metric.denominator(week))})
                    </span>
                ) : null}
            </span>
            <span className="block">
                <DeltaBadge
                    current={current}
                    previous={previous}
                    kind={metric.kind}
                    increaseIsGood={metric.increaseIsGood}
                />
            </span>
        </td>
    );
}

function WeekHeaderCells({ weeks }: { readonly weeks: Array<WeekTotals> }) {
    return weeks.map((week, index) => (
        <th
            key={week.weekStart}
            className="px-3 py-2 text-right font-medium whitespace-nowrap"
        >
            {weekLabel(week.weekStart)}
            {index === weeks.length - 1 ? (
                <span className="text-muted-foreground block text-[10px] font-normal">
                    partial
                </span>
            ) : null}
        </th>
    ));
}

export function WeeklyLaneComparison({
    section,
    weeks: selectedWeeks,
    onWeeksChange,
}: {
    readonly section: AutoOutreachWeeklySection | null;
    readonly weeks: number;
    readonly onWeeksChange: (weeks: number) => void;
}) {
    const [selectedLanes, setSelectedLanes] = useState<Set<OutreachLaneKey>>(
        new Set(OUTREACH_LANE_OPTIONS.map((option) => option.key)),
    );
    const weeksSelect = (
        <Select
            value={String(selectedWeeks)}
            onValueChange={(next) => onWeeksChange(Number(next))}
        >
            <SelectTrigger
                size="sm"
                className="w-36"
                aria-label="Weekly comparison span"
            >
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {WEEKLY_WEEKS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                        Last {option} weeks
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
    if (section === null) {
        return (
            <div className="flex w-full max-w-full min-w-0 flex-col gap-2">
                <div className="flex items-center justify-end">
                    {weeksSelect}
                </div>
                <div className="text-muted-foreground flex h-40 items-center justify-center rounded-md border text-sm">
                    Loading…
                </div>
            </div>
        );
    }
    const weeks = section.weeks.map((week) => sumLanes(week, selectedLanes));
    // The payload lags the selector while a new span computes (the trailing
    // scan can take a while), so flag the table as stale instead of silently
    // showing the old span.
    const isStaleSpan = section.weeks.length !== selectedWeeks;
    return (
        <div className="flex w-full max-w-full min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <LaneFilterChips
                    selected={selectedLanes}
                    onChange={setSelectedLanes}
                />
                <div className="flex items-center gap-2">
                    {isStaleSpan ? (
                        <span className="text-muted-foreground text-xs">
                            Loading last {selectedWeeks} weeks… showing last{' '}
                            {section.weeks.length} weeks
                        </span>
                    ) : null}
                    {weeksSelect}
                </div>
            </div>
            <div
                className={cn(
                    'w-full max-w-full min-w-0 overflow-x-auto rounded-md border',
                    isStaleSpan && 'opacity-60',
                )}
            >
                <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                        <tr className="text-muted-foreground text-left">
                            <th className="bg-muted sticky left-0 px-3 py-2 font-medium">
                                Metric
                            </th>
                            <WeekHeaderCells weeks={weeks} />
                        </tr>
                    </thead>
                    <tbody>
                        {METRIC_ROWS.map((metric) => (
                            <tr key={metric.key} className="border-t">
                                <td className="bg-background sticky left-0 px-3 py-2 font-medium whitespace-nowrap">
                                    {metric.label}
                                </td>
                                {weeks.map((week, index) => (
                                    <MetricCell
                                        key={week.weekStart}
                                        metric={metric}
                                        week={week}
                                        previousWeek={
                                            index > 0
                                                ? weeks[index - 1]
                                                : undefined
                                        }
                                    />
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
